import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MailCheck, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const email = loc.state?.email || params.get("email") || "";
  const role = loc.state?.role || params.get("role") || "customer";
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!email) { nav("/register", { replace: true }); return; }
  }, [email, nav]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const verify = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) { toast.error("الرمز مكوّن من 6 أرقام"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email, code: code.trim() });
      setSession(data.token, data.user);
      toast.success("تم تفعيل حسابك بنجاح!");
      nav(role === "seller" ? "/seller" : "/");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      await api.post("/auth/resend-code", { email });
      toast.success("تم إرسال رمز جديد إلى بريدك");
      setCooldown(60);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResending(false); }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-14">
      <div className="text-center mb-8">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--amber)] text-[#0B0F17] mb-3"><MailCheck size={28} /></span>
        <h1 className="font-head text-2xl font-black text-white">تفعيل البريد الإلكتروني</h1>
        <p className="text-sm text-[var(--text-muted)] mt-2">
          أرسلنا رمز تفعيل مكوّن من 6 أرقام إلى<br />
          <span className="text-white font-bold" data-testid="verify-email-address" dir="ltr">{email}</span>
        </p>
      </div>

      <form onSubmit={verify} className="ts-card p-6 space-y-5">
        <div>
          <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">رمز التفعيل</label>
          <input data-testid="verify-code-input" value={code} inputMode="numeric" maxLength={6} dir="ltr"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="______"
            className="w-full rounded-xl bg-[var(--bg-secondary)] px-4 py-3 text-center text-2xl tracking-[0.5em] font-black text-white outline-none" />
        </div>
        <button data-testid="verify-submit" disabled={loading} className="btn-amber w-full justify-center disabled:opacity-60">
          {loading ? "جارٍ التحقق..." : "تفعيل الحساب"}
        </button>

        <div className="text-center text-sm text-[var(--text-muted)]">
          لم يصلك الرمز؟{" "}
          <button type="button" data-testid="resend-code-btn" onClick={resend} disabled={cooldown > 0 || resending}
            className={`font-bold ${cooldown > 0 ? "text-[var(--text-subtle)] cursor-not-allowed" : "text-[var(--amber)]"}`}>
            {cooldown > 0 ? `إعادة الإرسال بعد ${cooldown} ثانية` : (resending ? "جارٍ الإرسال..." : "إعادة إرسال الرمز")}
          </button>
        </div>
      </form>

      <p className="text-center text-sm text-[var(--text-muted)] mt-6">
        <Link to="/register" className="text-[var(--amber)] font-bold"><Wrench size={14} className="inline" /> العودة للتسجيل</Link>
      </p>
    </div>
  );
}
